import React from 'react';
import {
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Typography,
  Button,
  Box,
  Chip,
  Avatar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
  Euro as EuroIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import './MissionCard.css';

const MissionCard = ({ mission, variant = 'default' }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!mission) return null;

  const {
    id,
    title,
    description,
    discipline,
    location,
    date,
    rate,
    image,
    organization,
    organizationLogo,
    type,
    status
  } = mission;

  const isCompact = variant === 'compact';

  return (
    <Card
      className={`mission-card ${isCompact ? 'mission-card--compact' : ''}`}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '16px',
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        border: '1px solid',
        borderColor: 'grey.200',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
          borderColor: 'primary.main',
        },
      }}
    >
      {/* Image */}
      <Box sx={{ position: 'relative' }}>
        <CardMedia
          component="img"
          height={isCompact ? "160" : "200"}
          image={image || 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400'}
          alt={title}
          sx={{
            objectFit: 'cover',
            transition: 'transform 0.3s ease',
          }}
        />
        
        {/* Status Badge */}
        {status && (
          <Chip
            label={status === 'active' ? 'Actif' : 'Fermé'}
            color={status === 'active' ? 'success' : 'default'}
            size="small"
            sx={
              {
                position: 'absolute',
                top: 12,
                right: 12,
                backgroundColor: status === 'active' ? 'success.main' : 'grey.500',
                color: 'white',
                fontWeight: 600,
              }
            }
          />
        )}
        
        {/* Type Badge */}
        {type && (
          <Chip
            label={type}
            size="small"
            sx={{
              position: 'absolute',
              top: 12,
              left: 12,
              backgroundColor: 'rgba(255,255,255,0.9)',
              color: 'text.primary',
              fontWeight: 500,
            }}
          />
        )}
      </Box>

      {/* Content */}
      <CardContent
        sx={{
          flexGrow: 1,
          p: isCompact ? 2 : 3,
          pb: isCompact ? 1 : 2,
        }}
      >
        {/* Title */}
        <Typography
          variant={isCompact ? "h6" : "h5"}
          component="h3"
          gutterBottom
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            mb: 1,
            lineHeight: 1.3,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {title}
        </Typography>

        {/* Description */}
        {!isCompact && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 2,
              lineHeight: 1.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {description}
          </Typography>
        )}

        {/* Discipline */}
        {discipline && (
          <Chip
            label={discipline}
            size="small"
            color="primary"
            variant="outlined"
            sx={{
              mb: 2,
              fontWeight: 500,
            }}
          />
        )}

        {/* Mission Details */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* Location */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {location && typeof location === 'object' && location.address 
                ? `${location.address.city}, ${location.address.postalCode}`
                : location || 'Localisation non spécifiée'
              }
            </Typography>
          </Box>

          {/* Date */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ScheduleIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              Date: {date}
            </Typography>
          </Box>

          {/* Rate */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EuroIcon sx={{ fontSize: 18, color: 'success.main' }} />
            <Typography
              variant="body2"
              sx={{
                color: 'success.main',
                fontWeight: 600,
              }}
            >
              Tarif: {rate}
            </Typography>
          </Box>

          {/* Organization */}
          {organization && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <Avatar 
                src={organizationLogo}
                sx={{ width: 24, height: 24, bgcolor: 'primary.main' }}
              >
                <BusinessIcon sx={{ fontSize: 14 }} />
              </Avatar>
              <Typography variant="body2" color="text.secondary">
                {organization?.name || organization}
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>

      {/* Actions */}
      <CardActions
        sx={{
          p: isCompact ? 2 : 3,
          pt: 0,
          justifyContent: 'center',
        }}
      >
        <Button
          component={Link}
          to={`/missions/${id}`}
          variant="contained"
          color="primary"
          fullWidth
          sx={{
            borderRadius: '25px',
            py: 1.5,
            fontWeight: 600,
            textTransform: 'none',
            fontSize: '1rem',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
              transform: 'translateY(-1px)',
            },
          }}
        >
          Voir détails
        </Button>
      </CardActions>
    </Card>
  );
};

export default MissionCard;